/**
 * HomeFaq — visible FAQ section + matching FAQPage JSON-LD on the home page.
 *
 * Why it lives on the home page
 * -----------------------------
 * The home page is the most-crawled URL. A FAQPage JSON-LD block here unlocks
 * Google's "People Also Ask" rich result and lifts the page's perceived
 * authority (more semantic content, more long-tail keyword coverage) without
 * touching the visual hierarchy of the hero/USP/collections.
 *
 * Why visible AND JSON-LD
 * -----------------------
 * Google policy: the answers in FAQPage schema MUST be visible on the page.
 * Putting them in an Accordion is acceptable — the markup is in the DOM at
 * load, just visually collapsed. We render full text inside <AccordionContent>
 * so the JSON-LD payload mirrors what crawlers see in the DOM.
 *
 * The matching JSON-LD is emitted from Home.jsx via the <Seo /> component so
 * it stays inside the page's <head> alongside the other schema graph nodes.
 */
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "../ui/accordion";

export const HOME_FAQS = [
    {
        q: "What makes MyEcoTour different from a regular travel agency?",
        a: "We do not resell off-the-shelf packages. Every itinerary is hand-drafted by a concierge for a single household, audited for its carbon footprint, and routed through pre-vetted lodges where at least 70% of the spend stays in the host community. Quiet, considered, and measurably better.",
    },
    {
        q: "Are your journeys really carbon-neutral?",
        a: "Yes. Every confirmed booking is measured by an independent climate auditor and offset through verified Indian reforestation projects via our Tree-Nation partnership. You receive a per-trip carbon report and a geo-tagged planting record before you depart.",
    },
    {
        q: "How do you select destinations, lodges and on-ground partners?",
        a: "Every property in our network has been personally visited and assessed against five rubrics — sustainability, community ownership, on-ground hospitality, accessibility and safety. We accept fewer than 20% of the venues we evaluate. Wildlife operations face an additional ethics audit; rides, performances and feeding interactions are non-negotiable disqualifiers.",
    },
    {
        q: "Can you customise a journey beyond what's on the website?",
        a: "Absolutely — the website is a starting point, not a catalogue. Tell us a season, a feeling, or a destination half-imagined, and a concierge will respond personally within one working day with a tailored proposal. We routinely design private wildlife, wellness, heritage, adventure, ocean and honeymoon journeys across India, Asia, Africa, Europe, the Americas and Oceania.",
    },
    {
        q: "What's typically included in the package price?",
        a: "All-inclusive by design: accommodation, meals at the property, scheduled experiences, on-ground transfers, English-speaking specialist guides where relevant, visa guidance, flight recommendations, 24/7 WhatsApp concierge support during travel, and the carbon offset. International flights and personal travel insurance are not included unless you specifically request them — we'll quote both transparently.",
    },
    {
        q: "How do I start planning my journey?",
        a: "Share a few details via the enquiry form — a destination, a season, a feeling, or simply a question. A concierge will write back within one working day. There is no obligation, no automated funnel, and your details are never sold or shared. WhatsApp +91 90048 50588 if you'd prefer a real-time conversation.",
    },
];

export const HOME_FAQ_JSONLD = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "@id": "https://myecotour.in/#faq",
    "mainEntity": HOME_FAQS.map(({ q, a }) => ({
        "@type": "Question",
        "name": q,
        "acceptedAnswer": { "@type": "Answer", "text": a },
    })),
};

export default function HomeFaq() {
    return (
        <section
            className="section-padding bg-background border-t border-border"
            data-testid="home-faq-section"
        >
            <div className="container-page grid grid-cols-1 lg:grid-cols-12 gap-12">
                <div className="lg:col-span-4">
                    <p className="overline text-muted-foreground mb-4">Questions answered</p>
                    <h2 className="font-serif text-4xl sm:text-5xl tracking-tight leading-[1.05]">
                        Why <em className="italic text-accent">MyEcoTour.</em>
                    </h2>
                    <p className="mt-6 text-foreground/75 leading-relaxed max-w-md">
                        The questions guests most often ask before reaching out — answered honestly, by the team that will design your journey.
                    </p>
                </div>
                <div className="lg:col-span-8">
                    <Accordion
                        type="single"
                        collapsible
                        className="divide-y divide-border"
                        data-testid="home-faq-accordion"
                    >
                        {HOME_FAQS.map(({ q, a }, idx) => (
                            <AccordionItem
                                key={idx}
                                value={`faq-${idx}`}
                                className="border-none"
                                data-testid={`home-faq-item-${idx}`}
                            >
                                <AccordionTrigger
                                    className="font-serif text-xl sm:text-2xl tracking-tight text-left hover:no-underline"
                                    data-testid={`home-faq-trigger-${idx}`}
                                >
                                    {q}
                                </AccordionTrigger>
                                <AccordionContent className="text-foreground/75 leading-relaxed text-base pb-6">
                                    {a}
                                </AccordionContent>
                            </AccordionItem>
                        ))}
                    </Accordion>
                </div>
            </div>
        </section>
    );
}
